import { ComponentProps, ComponentType, Dispatch, ReactNode, SetStateAction, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import {
	DiceIcon,
	LootboxIcon,
	CoinflipIcon,
	LimboIcon,
	PlinkoIcon,
	SuiroyaleIcon,
	UserIcon,
	LeftRightArrowIcon,
	GiftIcon,
	NftIcon,
	ReferralIcon,
	CrownIcon,
} from './icons';

import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Button, Badge } from './ui';
import { SocialMenu } from './social-menu';
import WelcomeModal from './welcome-modal';
import SaintPatrickWelcomeModal from './saint-patrick-welcome-modal';
import { SaintPatrickStreakLeaderCard } from './saint-patrick-streak-leader-card';
import { easeInOut, motion } from 'motion/react';
import { GAMES } from '@/constants/games';
import { ChevronLeft, ChevronRight, Play, X } from 'lucide-react';
import { cn } from '@/utils';
import { useDailyStreak } from '@/hooks/use-daily-streak';
import { QuestsIcon } from './icons/QuestsIcon';
import { useAuth } from '@/hooks';
import { useSuiClient } from '@mysten/dapp-kit';
import { useQuery } from '@tanstack/react-query';
import { vipRepository } from '@/repositories/vip-repository';
import { useVipMultipliers } from '@/hooks/use-vip-multipliers';
import { useAllQuests } from '@/hooks/use-game-quests';
import { useReferralData } from '@/hooks/use-referral-data';
import { useWeeklyContest } from '@/hooks/use-weekly-contest';
import { useCountdown } from '@/hooks/use-countdown';
import { useEventTheme } from '@/providers/EventThemeProvider';
import { useTheme } from 'next-themes';
import { useSidebarStore } from '@/stores/sidebar-store';
import { CAMPAIGN_CFG } from '@/configs/campaign-config';
import { NAV_FEATURES } from '@/constants/navFeatures';
import { useOneTimeFlag } from '@/hooks/use-one-time-flag';
import { useNftAttention } from '@/hooks/useNftAttention';
import { useCampaignModalStore } from '@/stores/campaign-modal-store';
import { useSaintPatrickModalStore } from '@/stores/saint-patrick-modal-store';
import { useCustomToast } from '@/hooks/use-suigar-toast';
import { campaignRepository } from '@/repositories/campaign-repository';
import { questRepository } from '@/repositories/quest-repository';
import { nftRepository } from '@/repositories/nft-repository';
import slushCoin from '@/assets/welcome-modal/slush.svg';
import ticketIconUrl from '@/suiroyale/assets/icons/ticket.svg';
import { SAINT_PATRICK_EVENT_CFG } from '@/configs/saint-patrick-event-config';
import { getTodayStamp, readDailyDismissRecord, shouldDisplayToday, writeDailyDismissRecord, type DailyDismissRecord } from '@/utils/daily-dismiss';
import { INTEGER_FORMATTER } from '@/utils/format-date-range';
import { isE2EEnabled } from '@/utils/e2e';
import {
	readSidebarCollapsedFromStorage,
	readSidebarExpandedCategoriesFromStorage,
	writeSidebarCollapsedToStorage,
	writeSidebarExpandedCategoriesToStorage,
} from '@/consts';

// MenuIcon component using individual icon components
const iconComponents: Record<string, ComponentType<{ className?: string }>> = {
	range: LeftRightArrowIcon,
	games: DiceIcon,
	lootbox: LootboxIcon,
	coinflip: CoinflipIcon,
	limbo: LimboIcon,
	plinko: PlinkoIcon,
	suiroyale: SuiroyaleIcon,
	user: UserIcon,
	leftRightArrow: LeftRightArrowIcon,
	gift: GiftIcon,
	quests: QuestsIcon,
	nft: NftIcon,
	referral: ReferralIcon,
	vip: CrownIcon,
};

const MenuIcon = ({ iconType, className }: { iconType: string; className?: string }) => {
	const IconComponent = iconComponents[iconType];
	if (!IconComponent) return null;

	return <IconComponent className={cn('inline-block size-5 shrink-0 text-current', className)} />;
};

const AttentionBadge = ({
	children,
	delay = 0.45,
	staggerIndex = 0,
	staggerGap = 0.18,
	...badgeProps
}: ComponentProps<typeof Badge> & { delay?: number; staggerIndex?: number; staggerGap?: number }) => {
	const effectiveDelay = delay + staggerIndex * staggerGap;

	return (
		<motion.span
			className="inline-flex"
			initial={{ opacity: 0, scale: 0.85, y: -6 }}
			animate={{ opacity: 1, scale: 1, y: 0 }}
			transition={{ delay: effectiveDelay, duration: 0.35, ease: easeInOut }}
		>
			<Badge {...badgeProps}>{children}</Badge>
		</motion.span>
	);
};

type TrailingBadgeDescriptor = {
	key: string;
	label: ReactNode;
	className?: string;
	variant?: ComponentProps<typeof Badge>['variant'];
};

type GameMenuItem = {
	name: string;
	to: string;
	iconType: string;
	isGame: boolean;
	isNew?: boolean;
};

type MenuCategorySection = {
	type: 'category';
	name: string;
	iconType: string;
	isExpanded?: boolean;
	items: GameMenuItem[];
};

type MenuLinkSection = {
	type: 'item';
	name: string;
	to: string;
	iconType: string;
	isGame: boolean;
	disabled?: boolean;
	comingSoonLabel?: string;
};

type MenuSection = MenuCategorySection | MenuLinkSection;

const NAV_ITEM_BASE_CLASSES =
	'group relative flex w-full items-center gap-2 rounded-lg border border-transparent px-3 py-2 text-sm font-medium transition-all';
const NAV_ITEM_ACTIVE_CLASSES = 'border-secondary/30 border-x-0 bg-secondary/60 text-primary-foreground shadow-sm hover:bg-secondary/60';
const NAV_ITEM_INACTIVE_CLASSES = 'bg-muted/25 text-muted-foreground hover:text-foreground hover:bg-muted/40';
const NAV_ITEM_OPEN_ACTIVE_CLASSES =
	'rounded-b-none border border-primary/70 border-b-0 bg-primary text-primary-foreground shadow-sm hover:bg-primary/95';
const NAV_ITEM_OPEN_INACTIVE_CLASSES = 'rounded-b-none border border-border/60 border-b-0 bg-muted/45 text-foreground shadow-sm hover:bg-muted/55';
const COLLAPSED_ITEM_BASE_CLASSES = 'flex h-10 w-10 items-center justify-center px-2 rounded-lg border border-transparent transition-colors';
const COLLAPSED_ITEM_ACTIVE_CLASSES = 'border-primary bg-primary text-primary-foreground shadow-sm';
const COLLAPSED_ITEM_INACTIVE_CLASSES = 'text-muted-foreground hover:text-foreground hover:bg-muted/30';
const CATEGORY_PANEL_OPEN_CLASSES = 'rounded-b-lg border border-border/60 border-t-0 bg-muted/30';

const MENU_UI_CLASSES = {
	nav: {
		base: NAV_ITEM_BASE_CLASSES,
		active: NAV_ITEM_ACTIVE_CLASSES,
		inactive: NAV_ITEM_INACTIVE_CLASSES,
		openActive: NAV_ITEM_OPEN_ACTIVE_CLASSES,
		openInactive: NAV_ITEM_OPEN_INACTIVE_CLASSES,
	},
	collapsed: {
		base: COLLAPSED_ITEM_BASE_CLASSES,
		active: COLLAPSED_ITEM_ACTIVE_CLASSES,
		inactive: COLLAPSED_ITEM_INACTIVE_CLASSES,
	},
};

const LIST_VARIANTS = {
	hidden: {},
	visible: { transition: { staggerChildren: 0.05, delayChildren: 0.2 } },
};

const ITEM_VARIANTS = {
	hidden: { opacity: 0, x: -24 },
	visible: {
		opacity: 1,
		x: 0,
		transition: { duration: 0.15, ease: easeInOut },
	},
};

const isGiveawayEnabled = NAV_FEATURES.giveaway.enabled;
const isGiveawayVisible = NAV_FEATURES.giveaway.visible;

const menuStructure: MenuSection[] = [
	{
		type: 'category',
		name: 'Games',
		iconType: 'games',
		isExpanded: true,
		items: GAMES.map((game) => ({
			name: game.name,
			to: game.link,
			iconType: game.id,
			isGame: game.isGame,
			isNew: game.isNew,
		})),
	},
	...(NAV_FEATURES.vip.visible
		? ([
				{
					type: 'item',
					name: 'VIP',
					to: '/vip',
					iconType: 'vip',
					isGame: false,
					disabled: !NAV_FEATURES.vip.enabled,
					comingSoonLabel: NAV_FEATURES.vip.comingSoonLabel,
				},
			] as MenuSection[])
		: []),
	...(NAV_FEATURES.weeklyRace.visible
		? ([{ type: 'item', name: 'Weekly Race', to: '/weekly-contest', iconType: 'gift', isGame: false }] as MenuSection[])
		: []),
	...(NAV_FEATURES.referral.visible
		? ([
				{
					type: 'item',
					name: 'Referral',
					to: '/referral',
					iconType: 'referral',
					isGame: false,
					disabled: !NAV_FEATURES.referral.enabled,
					comingSoonLabel: NAV_FEATURES.referral.comingSoonLabel,
				},
			] as MenuSection[])
		: []),
	...(NAV_FEATURES.quests.visible
		? ([
				{
					type: 'item',
					name: 'Quests',
					to: '/quests',
					iconType: 'quests',
					isGame: false,
					disabled: !NAV_FEATURES.quests.enabled,
					comingSoonLabel: NAV_FEATURES.quests.comingSoonLabel,
				},
			] as MenuSection[])
		: []),
	...(isGiveawayEnabled && isGiveawayVisible
		? ([
				{
					type: 'item',
					name: 'Giveaway',
					to: '/giveaway',
					iconType: 'gift',
					isGame: false,
					disabled: !NAV_FEATURES.giveaway.enabled,
					comingSoonLabel: NAV_FEATURES.giveaway.comingSoonLabel,
				},
			] as MenuSection[])
		: []),
	{ type: 'item', name: 'NFT', to: '/nft', iconType: 'nft', isGame: false },
];

const FIRST_CATEGORY_NAME = menuStructure.find((section) => section.type === 'category')?.name ?? null;

// Legacy structure for compatibility with hot game calculation
const menuItems: GameMenuItem[] = GAMES.map((game) => ({
	name: game.name,
	to: game.link,
	iconType: game.id,
	isGame: game.isGame,
}));

// Set a constant for the modal version date (update this to force showing the modal to everyone)
const MODAL_VERSION_DATE = '2025-05-22T15:28:00.000Z'; // ISO string, update as needed
const WELCOME_MODAL_STORAGE_KEY = 'suigar.dismiss.welcome-modal';
const SAINT_PATRICK_MODAL_VERSION_DATE = '2026-03-15T09:00:00.000Z';
const SAINT_PATRICK_WELCOME_MODAL_STORAGE_KEY = 'suigar.dismiss.saint-patrick-welcome-modal';

// Utility to pick a "hot" game index based on the current date and a period (in days)
// Only consider items where isGame is true
function getHotGameIndex(menuItems: GameMenuItem[], periodDays = 3) {
	const gameItems = menuItems.filter((item) => item.isGame);
	if (gameItems.length === 0) {
		return { hotIndex: 0, gameItems };
	}
	const now = new Date();
	// Calculate the number of periods since epoch
	const period = Math.floor(now.getTime() / (1000 * 60 * 60 * 24 * periodDays));
	// Deterministically pick an index
	return { hotIndex: period % gameItems.length, gameItems };
}

export function SideMenu({ onCollapseChange }: { onCollapseChange?: (collapsed: boolean) => void }) {
	const { event } = useEventTheme();
	const { resolvedTheme } = useTheme();
	const isDarkTheme = resolvedTheme === 'dark';
	const isMobileSidebarOpen = useSidebarStore((s) => s.isMobileOpen);
	const closeMobileSidebar = useSidebarStore((s) => s.close);
	const navigate = useNavigate();
	const suigarLogoFullUrl = useMemo(
		() => (isDarkTheme ? event.assets.logoFull.dark : event.assets.logoFull.light),
		[event.assets.logoFull.dark, event.assets.logoFull.light, isDarkTheme],
	);
	const suigarIconUrl = useMemo(
		() => (isDarkTheme ? event.assets.icon.light : event.assets.icon.default),
		[event.assets.icon.default, event.assets.icon.light, isDarkTheme],
	);
	const saintPatrickPromoButtonConfig = SAINT_PATRICK_EVENT_CFG.promoButton;
	const saintPatrickPromoFlag = useOneTimeFlag(saintPatrickPromoButtonConfig?.id);
	const showSaintPatrickPromoPulse = Boolean(saintPatrickPromoButtonConfig && !saintPatrickPromoFlag.hasSeen);
	const promoButtonConfig = CAMPAIGN_CFG.promoButton;
	const promoFlag = useOneTimeFlag(promoButtonConfig?.id);
	const showPromoPulse = Boolean(promoButtonConfig && !promoFlag.hasSeen);
	const { shouldShowNftBadge, dismissNftBadge } = useNftAttention();
	const capyTop = event.assets.suifrenImages.capyTop;
	const capyTopSurprised = event.assets.suifrenImages.capyTopSurprised;
	const [expandedCategories, setExpandedCategoriesState] = useState<Set<string>>(readSidebarExpandedCategoriesFromStorage);
	const [isCollapsed, setIsCollapsedState] = useState<boolean>(readSidebarCollapsedFromStorage);
	const [animatingCategories, setAnimatingCategories] = useState<Set<string>>(new Set());
	const [hoveredGame, setHoveredGame] = useState<string | null>(null);
	const closeSidebarForMobile = useCallback(() => {
		if (typeof window !== 'undefined' && window.innerWidth < 768) {
			closeMobileSidebar();
		}
	}, [closeMobileSidebar]);

	const setCollapsed = useCallback(
		(next: boolean) => {
			setIsCollapsedState(next);
			writeSidebarCollapsedToStorage(next);
			onCollapseChange?.(next);
		},
		[onCollapseChange],
	);

	const setExpandedCategoriesPersisted = useCallback((next: Set<string>) => {
		setExpandedCategoriesState(next);
		writeSidebarExpandedCategoriesToStorage(next);
	}, []);

	// Handle responsive behavior - uncollapse when switching to mobile
	useEffect(() => {
		if (typeof window === 'undefined') return;
		const handleResize = () => {
			const isMobile = window.innerWidth < 768;
			if (isMobile && isCollapsed) {
				setCollapsed(false);
			}
			if (!isMobile && isMobileSidebarOpen) {
				closeMobileSidebar();
			}
		};

		window.addEventListener('resize', handleResize);
		handleResize();

		return () => window.removeEventListener('resize', handleResize);
	}, [closeMobileSidebar, isCollapsed, isMobileSidebarOpen, setCollapsed]);

	const location = useLocation();
	const [isCapySurprised, setIsCapySurprised] = useState(false);
	const currentCapyImage = isCapySurprised ? (capyTopSurprised ?? capyTop) : capyTop;
	const showCapyAboveMenu = !isCollapsed && !saintPatrickPromoButtonConfig && Boolean(currentCapyImage);
	const sidebarListPaddingClass = showCapyAboveMenu ? 'pt-8.5' : isCollapsed ? 'pt-3' : 'pt-6';

	useEffect(() => {
		closeSidebarForMobile();
	}, [location.pathname, closeSidebarForMobile]);

	useEffect(() => {
		if (!isMobileSidebarOpen || typeof document === 'undefined') return;
		const { body } = document;
		const previousOverflow = body.style.overflow;
		body.style.overflow = 'hidden';
		return () => {
			body.style.overflow = previousOverflow;
		};
	}, [isMobileSidebarOpen]);
	useEffect(() => {
		if (!isMobileSidebarOpen || typeof window === 'undefined') return;
		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') {
				closeMobileSidebar();
			}
		};
		window.addEventListener('keydown', handleKeyDown);
		return () => window.removeEventListener('keydown', handleKeyDown);
	}, [isMobileSidebarOpen, closeMobileSidebar]);
	const {
		isOpen: isSaintPatrickModalOpen,
		setOpen: setSaintPatrickModalOpen,
		open: openSaintPatrickModal,
		close: closeSaintPatrickModal,
	} = useSaintPatrickModalStore();
	const { isOpen: isCampaignModalOpen, setOpen: setCampaignModalOpen, open: openCampaignModal, close: closeCampaignModal } = useCampaignModalStore();
	const toast = useCustomToast();
	const ticketing = CAMPAIGN_CFG.ticketing;
	const saintPatrickTicketing = SAINT_PATRICK_EVENT_CFG.ticketing;
	const saintPatrickQualification = SAINT_PATRICK_EVENT_CFG.qualification;
	const [saintPatrickWelcomeDismissRecord, setSaintPatrickWelcomeDismissRecord] = useState<DailyDismissRecord | null>(() =>
		readDailyDismissRecord(SAINT_PATRICK_WELCOME_MODAL_STORAGE_KEY),
	);
	const [welcomeDismissRecord, setWelcomeDismissRecord] = useState<DailyDismissRecord | null>(() =>
		readDailyDismissRecord(WELCOME_MODAL_STORAGE_KEY),
	);
	const [todayStamp, setTodayStamp] = useState(getTodayStamp());

	useEffect(() => {
		const interval = setInterval(() => {
			const next = getTodayStamp();
			setTodayStamp((prev) => (prev === next ? prev : next));
		}, 60 * 1000);
		return () => clearInterval(interval);
	}, []);

	useEffect(() => {
		if (typeof window === 'undefined') return;
		const handleStorage = (event: StorageEvent) => {
			if (event.key === SAINT_PATRICK_WELCOME_MODAL_STORAGE_KEY) {
				setSaintPatrickWelcomeDismissRecord(readDailyDismissRecord(SAINT_PATRICK_WELCOME_MODAL_STORAGE_KEY));
			}
			if (event.key === WELCOME_MODAL_STORAGE_KEY) {
				setWelcomeDismissRecord(readDailyDismissRecord(WELCOME_MODAL_STORAGE_KEY));
			}
		};
		window.addEventListener('storage', handleStorage);
		return () => window.removeEventListener('storage', handleStorage);
	}, []);

	const shouldOpenSaintPatrickModal = useMemo(() => {
		if (isE2EEnabled()) {
			return false;
		}
		if (SAINT_PATRICK_EVENT_CFG.state !== 'live') {
			return false;
		}
		return shouldDisplayToday(saintPatrickWelcomeDismissRecord, SAINT_PATRICK_MODAL_VERSION_DATE);
	}, [saintPatrickWelcomeDismissRecord, todayStamp]);

	useEffect(() => {
		if (shouldOpenSaintPatrickModal) {
			setSaintPatrickModalOpen(true);
		}
	}, [setSaintPatrickModalOpen, shouldOpenSaintPatrickModal]);

	const shouldOpenCampaignModal = useMemo(() => {
		if (isE2EEnabled()) {
			return false;
		}
		if (CAMPAIGN_CFG.features.campaignWalletEnabled === false) {
			return false;
		}
		return shouldDisplayToday(welcomeDismissRecord, MODAL_VERSION_DATE);
	}, [welcomeDismissRecord, todayStamp]);

	useEffect(() => {
		if (shouldOpenCampaignModal) {
			setCampaignModalOpen(true);
		}
	}, [shouldOpenCampaignModal, setCampaignModalOpen]);

	// Auth + client
	const { isLogged, address } = useAuth();
	const ticketToastFlag = useOneTimeFlag(address ? `${CAMPAIGN_CFG.id}.first-ticket.${address.toLowerCase()}` : null);
	const firstTicketToastSeen = ticketToastFlag.hasSeen;
	const markFirstTicketToastSeen = ticketToastFlag.markSeen;
	const client = useSuiClient();
	const canFetchSaintPatrickTickets = Boolean(saintPatrickPromoButtonConfig && isLogged && address);

	// VIP profile (level)
	const { data: vipProfile } = useQuery({
		queryKey: ['VipProfile', address],
		queryFn: () => (address ? vipRepository.getPlayerVipProfile(client, address) : null),
		enabled: !!address,
		staleTime: 60_000,
		refetchInterval: 120_000,
	});

	// XP multipliers
	const { data: vipMultipliers } = useVipMultipliers();
	const maxXpMultiplier = (vipMultipliers ?? []).reduce((m, e) => Math.max(m, e.multiplier || 0), 0);

	// Quests claimable
	const { questsByGame } = useAllQuests('ALL_TIME');
	const hasClaimableQuests = isLogged
		? Object.values(questsByGame || {}).some((qs: any) => (qs as any[]).some((q) => q.claimStatus === 'ready_to_claim'))
		: false;

	// Referral $ pending
	const { pendingAmount } = useReferralData();
	const hasReferralToClaim = (pendingAmount || 0) > 10;

	// Weekly race: time remaining until end
	const { data: weeklyData } = useWeeklyContest();
	const endAt = weeklyData?.snapshot?.endAt ?? null;
	const countdown = useCountdown(endAt ?? null, 1000);
	const weeklyTimeLeftShort = (() => {
		if (!endAt) return '';
		if (countdown.isExpired) return '0s';
		if (countdown.days > 0) return `${countdown.days}d`;
		if (countdown.hours > 0) return `${countdown.hours}h`;
		if (countdown.minutes > 0) return `${countdown.minutes}m`;
		return `${countdown.seconds}s`;
	})();

	const { data: campaignTicketSummary } = useQuery({
		queryKey: ['campaign', CAMPAIGN_CFG.id, 'tickets', address],
		queryFn: () =>
			campaignRepository.getTicketSummary({
				address: address!,
				start: CAMPAIGN_CFG.timeframe.start,
				end: CAMPAIGN_CFG.timeframe.end,
			}),
		enabled: Boolean(address) && CAMPAIGN_CFG.features.campaignWalletEnabled,
		staleTime: 20_000,
		refetchInterval: 20_000,
	});

	const saintPatrickQualificationQuery = useQuery({
		queryKey: ['saint-patrick', 'qualification', address],
		queryFn: async () => {
			const response = await questRepository.checkQuestStatus(SAINT_PATRICK_EVENT_CFG.slug, saintPatrickQualification.questKey, address!);
			return response.result;
		},
		enabled: canFetchSaintPatrickTickets,
		staleTime: 20_000,
		refetchInterval: canFetchSaintPatrickTickets ? 20_000 : false,
	});

	const saintPatrickTicketSummaryQuery = useQuery({
		queryKey: ['saint-patrick', 'wager', address],
		queryFn: () =>
			campaignRepository.getTicketSummary({
				address: address!,
				start: SAINT_PATRICK_EVENT_CFG.timeframe.start,
				end: SAINT_PATRICK_EVENT_CFG.timeframe.end,
				path: saintPatrickTicketing.apiPath,
			}),
		enabled: canFetchSaintPatrickTickets,
		staleTime: 20_000,
		refetchInterval: canFetchSaintPatrickTickets ? 20_000 : false,
	});

	const saintPatrickOwnedNftsQuery = useQuery({
		queryKey: ['saint-patrick', 'owned-nfts', address],
		queryFn: () => nftRepository.getOwnedNft(client, address!),
		enabled: canFetchSaintPatrickTickets,
		staleTime: 2 * 60_000,
	});

	const saintPatrickOwnedNames = useMemo(
		() => saintPatrickOwnedNftsQuery.data?.map((nft) => nft.name.toLowerCase()) ?? [],
		[saintPatrickOwnedNftsQuery.data],
	);
	const saintPatrickOwnedMultipliers = useMemo(
		() =>
			saintPatrickTicketing.bonusNfts
				.filter((bonus) => {
					const matcher = bonus.nameIncludes.toLowerCase().trim();
					return saintPatrickOwnedNames.some((name) => name.includes(matcher));
				})
				.map((bonus) => bonus.multiplier),
		[saintPatrickOwnedNames, saintPatrickTicketing.bonusNfts],
	);
	const saintPatrickCombinedBonusMultiplier =
		saintPatrickOwnedMultipliers.length === 0
			? 1
			: saintPatrickTicketing.bonusCombinationStrategy === 'multiply'
				? saintPatrickOwnedMultipliers.reduce((acc, value) => acc * value, 1)
				: Math.max(...saintPatrickOwnedMultipliers);
	const saintPatrickFinalMultiplier = saintPatrickTicketing.baseMultiplier * saintPatrickCombinedBonusMultiplier;
	const saintPatrickIsQualified = Boolean(saintPatrickQualificationQuery.data?.isValid);
	const saintPatrickTotalWagered = saintPatrickTicketSummaryQuery.data?.totalWagered ?? 0;
	const saintPatrickBonusTickets =
		saintPatrickTicketing.additionalTicketStep > 0 ? Math.floor(saintPatrickTotalWagered / saintPatrickTicketing.additionalTicketStep) : 0;
	const saintPatrickBaseTickets = saintPatrickIsQualified ? 1 + saintPatrickBonusTickets : 0;
	const saintPatrickFinalTickets = Math.round(saintPatrickBaseTickets * saintPatrickFinalMultiplier);
	const isSaintPatrickTicketCountLoading =
		canFetchSaintPatrickTickets &&
		(saintPatrickQualificationQuery.isLoading || saintPatrickTicketSummaryQuery.isLoading || saintPatrickOwnedNftsQuery.isLoading);
	const saintPatrickPromoTicketBadge = saintPatrickPromoButtonConfig && isLogged ? (
		<PromoTicketBadge countLabel={isSaintPatrickTicketCountLoading ? '...' : INTEGER_FORMATTER.format(saintPatrickFinalTickets)} />
	) : null;

	useEffect(() => {
		if (!address || !campaignTicketSummary || firstTicketToastSeen || !CAMPAIGN_CFG.features.campaignWalletEnabled) return;
		if (campaignTicketSummary.totalWagered >= ticketing.firstTicketThreshold) {
			toast({
				title: 'You’re eligible for the Slush Wallet draw!',
				description: `You wagered at least ${ticketing.firstTicketThreshold} ${ticketing.wageringCoinSymbol}. Play more to get more tickets!`,
				status: 'success',
			});
			markFirstTicketToastSeen();
		}
	}, [
		address,
		campaignTicketSummary?.totalWagered,
		firstTicketToastSeen,
		markFirstTicketToastSeen,
		ticketing.firstTicketThreshold,
		ticketing.wageringCoinSymbol,
		toast,
	]);

	const handleWelcomeModalClose = () => {
		const record: DailyDismissRecord = { day: getTodayStamp(), version: MODAL_VERSION_DATE };
		writeDailyDismissRecord(WELCOME_MODAL_STORAGE_KEY, record);
		setWelcomeDismissRecord(record);
		closeCampaignModal();
	};

	const handleSaintPatrickWelcomeModalClose = () => {
		const record: DailyDismissRecord = { day: getTodayStamp(), version: SAINT_PATRICK_MODAL_VERSION_DATE };
		writeDailyDismissRecord(SAINT_PATRICK_WELCOME_MODAL_STORAGE_KEY, record);
		setSaintPatrickWelcomeDismissRecord(record);
		closeSaintPatrickModal();
	};

	const handleSaintPatrickPromoButtonClick = () => {
		openSaintPatrickModal();
		if (!saintPatrickPromoFlag.hasSeen) {
			saintPatrickPromoFlag.markSeen();
		}
		closeSidebarForMobile();
	};

	const handlePromoButtonClick = () => {
		if (promoButtonConfig?.to) {
			navigate({ to: promoButtonConfig.to });
		} else {
			openCampaignModal();
		}
		if (!promoFlag.hasSeen) {
			promoFlag.markSeen();
		}
		closeSidebarForMobile();
	};

	useEffect(() => {
		if (isCollapsed) {
			setIsCapySurprised(false);
			return;
		}
		if (typeof window === 'undefined') return;

		const timeouts = new Set<number>();
		const schedule = (fn: () => void, delayMs: number) => {
			const id = window.setTimeout(() => {
				timeouts.delete(id);
				fn();
			}, delayMs);
			timeouts.add(id);
		};

		const randomImageSwitcher = () => {
			const shouldSurpriseAppear = Math.random() < 0.2;

			if (shouldSurpriseAppear) {
				setIsCapySurprised(true);

				schedule(() => setIsCapySurprised(false), 2000);
				schedule(() => setIsCapySurprised(true), 2100);
				schedule(() => setIsCapySurprised(false), 3000);
			}
		};

		const interval = window.setInterval(randomImageSwitcher, Math.random() * 1000 + 5000);

		return () => {
			window.clearInterval(interval);
			timeouts.forEach((timeoutId) => window.clearTimeout(timeoutId));
		};
	}, [isCollapsed]);

	// Determine which game is "hot" for this period
	const { hotIndex, gameItems } = getHotGameIndex(menuItems);
	const isBrowser = typeof window !== 'undefined';
	const isCompactHeight = isBrowser && window.innerHeight <= 796;

	// Toggle category expansion
	const toggleCategory = (categoryName: string) => {
		const newExpanded = new Set(expandedCategories);
		const newAnimating = new Set(animatingCategories);

		if (newExpanded.has(categoryName)) {
			newExpanded.delete(categoryName);
		} else {
			newExpanded.add(categoryName);
		}

		newAnimating.add(categoryName);
		setExpandedCategoriesPersisted(newExpanded);
		setAnimatingCategories(newAnimating);
	};

	const openCategory = (categoryName: string) => {
		if (expandedCategories.has(categoryName)) return;
		const newExpanded = new Set(expandedCategories);
		const newAnimating = new Set(animatingCategories);
		newExpanded.add(categoryName);
		newAnimating.add(categoryName);
		setExpandedCategoriesPersisted(newExpanded);
		setAnimatingCategories(newAnimating);
	};

	const toggleSidebar = () => {
		setCollapsed(!isCollapsed);
	};

	const handleCategoryAnimationComplete = useCallback((categoryName: string) => {
		setAnimatingCategories((prev) => {
			const next = new Set(prev);
			next.delete(categoryName);
			return next;
		});
	}, []);

	// Animation variants for menu items and social icons
	return (
		<>
			{isMobileSidebarOpen ? (
				<div className="fixed inset-0 z-30 bg-background/80 backdrop-blur-sm md:hidden" aria-hidden="true" onClick={closeMobileSidebar} />
			) : null}
			<aside
				id="default-sidebar"
				className={cn(
					'fixed left-0 top-0 z-40 flex h-full bg-background transition-all duration-300 max-md:w-screen md:translate-x-0 md:border-r md:border-border/60',
					isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full',
					isCollapsed ? 'md:w-18' : 'md:w-52',
				)}
				aria-label="Sidebar"
			>
				{/* Mobile close button */}
				<button
					type="button"
					aria-controls="default-sidebar"
					className="group absolute end-5 top-5 inline-flex items-center rounded-md border border-border/60 bg-muted/40 p-1.5 text-sm hover:cursor-pointer! hover:bg-muted/60 md:hidden"
					onClick={closeMobileSidebar}
				>
					<span className="sr-only">Close sidebar</span>
					<X className="h-5 w-5" />
					<span className="sr-only">Close menu</span>
				</button>

				{/* Desktop collapse toggle */}
				<button
					onClick={toggleSidebar}
					className="absolute -right-3 top-16 z-10 hidden rounded-full border border-border/60 bg-muted/60 p-0.5 backdrop-blur-sm transition-colors hover:bg-muted/80 md:block cursor-pointer"
				>
					<motion.div animate={{ rotate: isCollapsed ? 180 : 0 }} transition={{ duration: 0.2 }}>
						<ChevronLeft className="h-4 w-4" />
					</motion.div>
				</button>

				<div className="flex w-full flex-col justify-between items-center py-4">
					<Link
						to="/"
						className={`cursor-pointer transition-transform md:hover:scale-95 max-md:max-w-80 ${isCollapsed ? 'w-10' : 'mb-2 px-3'}`}
						aria-controls="default-sidebar"
						onClick={closeSidebarForMobile}
					>
						<motion.img
							className="w-full"
							src={isCollapsed ? suigarIconUrl : suigarLogoFullUrl}
							alt="Suigar"
							loading="lazy"
							initial={{ opacity: 0, y: 16 }}
							animate={{ opacity: 1, y: 0 }}
							transition={{ duration: 0.5, ease: easeInOut }}
						/>
					</Link>
					<div className={`flex flex-col w-full grow ${isCollapsed ? '' : 'overflow-y-auto overflow-x-hidden'} px-4`}>
						<div className={cn('relative')}>
							<motion.ul
								className={cn(
									'font-large space-y-1.5 max-md:mx-auto max-md:max-w-96 max-sm:max-w-80',
									isCollapsed ? 'w-10' : 'w-full',
									sidebarListPaddingClass,
								)}
								variants={LIST_VARIANTS}
								initial="hidden"
								animate="visible"
							>
									{saintPatrickPromoButtonConfig ? (
										<PromoMenuButton
											key="saint-patrick-promo"
											isCollapsed={isCollapsed}
											menuClasses={MENU_UI_CLASSES}
											label={saintPatrickPromoButtonConfig.label}
											onClick={handleSaintPatrickPromoButtonClick}
											showPulse={showSaintPatrickPromoPulse}
											imageSrc={SAINT_PATRICK_EVENT_CFG.artwork.buttonAccent}
											imageAlt={SAINT_PATRICK_EVENT_CFG.name}
											iconSrc={SAINT_PATRICK_EVENT_CFG.artwork.buttonIcon}
											imageClassName="right-6 w-10 -translate-y-[85%] rotate-0 transition-transform duration-300 ease-out group-hover/promo:translate-x-1 group-hover/promo:-translate-y-[92%] group-hover/promo:rotate-12"
											iconClassName="scale-125"
											trailingBadge={saintPatrickPromoTicketBadge}
											useImageIcon
										/>
									) : null}
									{saintPatrickPromoButtonConfig && !isCollapsed ? (
										<motion.li key="saint-patrick-leader" variants={ITEM_VARIANTS}>
											<SaintPatrickStreakLeaderCard variant="sidebar" onClick={handleSaintPatrickPromoButtonClick} />
										</motion.li>
									) : null}
									{promoButtonConfig ? (
										<PromoMenuButton
										key="slush-wallet-promo"
										isCollapsed={isCollapsed}
										menuClasses={MENU_UI_CLASSES}
										label={promoButtonConfig.label}
										onClick={handlePromoButtonClick}
										showPulse={showPromoPulse}
									/>
								) : null}
								{menuStructure.map((section) =>
									section.type === 'category' ? (
										<MenuCategory
											key={section.name}
											section={section}
											isExpanded={expandedCategories.has(section.name)}
											isCollapsed={isCollapsed}
											isCompactHeight={isCompactHeight}
											isAnimating={animatingCategories.has(section.name)}
											locationPath={location.pathname}
											onToggle={() => toggleCategory(section.name)}
											onOpen={() => openCategory(section.name)}
											onAnimationComplete={() => handleCategoryAnimationComplete(section.name)}
											menuClasses={MENU_UI_CLASSES}
											closeSidebarForMobile={closeSidebarForMobile}
											hoveredGame={hoveredGame}
											setHoveredGame={setHoveredGame}
											gameContext={{ gameItems, hotIndex, maxXpMultiplier }}
											hasClaimableQuests={hasClaimableQuests}
											hasReferralToClaim={hasReferralToClaim}
											showCapyAboveMenu={showCapyAboveMenu && section.name === FIRST_CATEGORY_NAME}
											capyImage={currentCapyImage}
										/>
									) : (
										<MenuLinkItem
											key={section.name}
											section={section}
											isCollapsed={isCollapsed}
											isCompactHeight={isCompactHeight}
											locationPath={location.pathname}
											closeSidebarForMobile={closeSidebarForMobile}
											menuClasses={MENU_UI_CLASSES}
											vipProfileLevel={vipProfile?.level ?? 0}
											weeklyTimeLeftShort={weeklyTimeLeftShort}
											hasClaimableQuests={hasClaimableQuests}
											hasReferralToClaim={hasReferralToClaim}
											nftBadgeConfig={
												section.name === 'NFT'
													? {
															show: shouldShowNftBadge,
															dismiss: dismissNftBadge,
														}
													: undefined
											}
										/>
									),
								)}
							</motion.ul>
						</div>
					</div>
					<motion.div className="mt-4" variants={LIST_VARIANTS} initial="hidden" animate="visible">
						<motion.div className={`flex w-full justify-center gap-2 md:justify-around ${isCollapsed ? 'flex-col' : ''} `} variants={ITEM_VARIANTS}>
							<SocialMenu size="sm" collapsed={isCollapsed} />
						</motion.div>
					</motion.div>
				</div>
			</aside>
			<SaintPatrickWelcomeModal isOpen={isSaintPatrickModalOpen} onClose={handleSaintPatrickWelcomeModalClose} />
			<WelcomeModal isOpen={isCampaignModalOpen} onClose={handleWelcomeModalClose} />
		</>
	);
}

type MenuUiClasses = typeof MENU_UI_CLASSES;

type GameMenuItemProps = {
	item: MenuCategorySection['items'][number];
	isCollapsed: boolean;
	isCompactHeight: boolean;
	isActive: boolean;
	isHot: boolean;
	menuClasses: MenuUiClasses;
	trailingBadges: TrailingBadgeDescriptor[];
	hoveredGame: string | null;
	setHoveredGame: Dispatch<SetStateAction<string | null>>;
	closeSidebarForMobile: () => void;
};

function GameMenuItem({
	item,
	isCollapsed,
	isCompactHeight,
	isActive,
	isHot,
	menuClasses,
	trailingBadges,
	hoveredGame,
	setHoveredGame,
	closeSidebarForMobile,
}: GameMenuItemProps) {
	const { nav, collapsed } = menuClasses;
	const hasTrailingBadges = trailingBadges.length > 0;
	const isHovered = hoveredGame === item.name;
	const showPlayIcon = isHovered && !isActive;

	if (isCollapsed) {
		return (
			<motion.div className="relative" variants={ITEM_VARIANTS} data-hot={isHot || undefined}>
				<Tooltip>
					<TooltipTrigger asChild>
						<Link
							to={item.to}
							className={cn(collapsed.base, isActive ? collapsed.active : collapsed.inactive)}
							aria-controls="default-sidebar"
							aria-label={item.name}
							onClick={closeSidebarForMobile}
						>
							<MenuIcon
								iconType={item.iconType}
								className={cn('inline-block size-5', isActive ? 'text-primary-foreground' : 'text-muted-foreground')}
							/>
						</Link>
					</TooltipTrigger>
					<TooltipContent side="right">
						<p>{item.name}</p>
					</TooltipContent>
				</Tooltip>
			</motion.div>
		);
	}

	return (
		<motion.div className="relative" variants={ITEM_VARIANTS} data-hot={isHot || undefined}>
			<Button
				asChild
				variant="ghost"
				size="sm"
				className={cn('rounded-none!', nav.base, isActive ? nav.active : nav.inactive, isCompactHeight ? 'px-2 py-1.5' : '')}
			>
				<Link
					to={item.to}
					className="flex w-full items-center justify-start gap-2"
					aria-controls="default-sidebar"
					onMouseEnter={() => setHoveredGame(item.name)}
					onMouseLeave={() => setHoveredGame((current) => (current === item.name ? null : current))}
					onFocus={() => setHoveredGame(item.name)}
					onBlur={() => setHoveredGame((current) => (current === item.name ? null : current))}
					onClick={closeSidebarForMobile}
				>
					<MenuIcon iconType={item.iconType} className={cn('inline-block size-5', isActive ? 'text-primary-foreground' : 'text-muted-foreground')} />
					<span className="text-sm">{item.name}</span>
					<div className={cn('ml-auto flex items-center', hasTrailingBadges && !showPlayIcon ? 'gap-1' : '')}>
						{hasTrailingBadges &&
							!showPlayIcon &&
							trailingBadges.map((badge, badgeIndex) => (
								<AttentionBadge
									key={badge.key}
									variant={badge.variant ?? 'secondary'}
									className={badge.className ?? 'rotate-2 text-[10px] font-bold py-0 px-1'}
									delay={0.6}
									staggerIndex={badgeIndex}
								>
									{badge.label}
								</AttentionBadge>
							))}
						<motion.span
							className="pointer-events-none flex h-4 items-center justify-center overflow-hidden text-secondary"
							initial={false}
							animate={showPlayIcon ? { opacity: 1, x: 0, width: 16 } : { opacity: 0, x: 12, width: 0 }}
							transition={{ duration: 0.2, ease: easeInOut }}
						>
							<Play className="h-4 w-4" />
						</motion.span>
					</div>
				</Link>
			</Button>
		</motion.div>
	);
}

type MenuCategoryProps = {
	section: MenuCategorySection;
	isExpanded: boolean;
	isCollapsed: boolean;
	isCompactHeight: boolean;
	isAnimating: boolean;
	locationPath: string;
	onToggle: () => void;
	onOpen: () => void;
	onAnimationComplete: () => void;
	menuClasses: MenuUiClasses;
	closeSidebarForMobile: () => void;
	hoveredGame: string | null;
	setHoveredGame: Dispatch<SetStateAction<string | null>>;
	gameContext: { gameItems: GameMenuItem[]; hotIndex: number; maxXpMultiplier: number };
	hasClaimableQuests: boolean;
	hasReferralToClaim: boolean;
	showCapyAboveMenu?: boolean;
	capyImage?: string | null;
};

function MenuCategory({
	section,
	isExpanded,
	isCollapsed,
	isCompactHeight,
	isAnimating,
	locationPath,
	onToggle,
	onOpen,
	onAnimationComplete,
	menuClasses,
	closeSidebarForMobile,
	hoveredGame,
	setHoveredGame,
	gameContext,
	hasClaimableQuests,
	hasReferralToClaim,
	showCapyAboveMenu,
	capyImage,
}: MenuCategoryProps) {
	const { nav, collapsed } = menuClasses;
	const { gameItems, hotIndex, maxXpMultiplier } = gameContext;
	const hotGameName = gameItems[hotIndex]?.name ?? null;
	const isOpen = isExpanded && !isCollapsed;
	const isGamesCategory = section.name === 'Games';
	const isCategoryRouteActive = isGamesCategory ? locationPath.startsWith('/games') : false;

	const triggerClasses = cn(
		nav.base,
		isCompactHeight ? 'px-2 py-1.5' : '',
		isOpen ? (isCategoryRouteActive ? nav.openActive : nav.openInactive) : isCategoryRouteActive ? nav.active : nav.inactive,
		'justify-between',
	);

	const collapsedTriggerClasses = cn(collapsed.base);

	const wrapperClasses = cn(isCollapsed ? '' : '-mr-3 pr-3', (isAnimating || !isExpanded) && 'overflow-hidden');
	const panelClasses = cn(
		!isCollapsed && 'overflow-hidden',
		isOpen && !isCollapsed ? CATEGORY_PANEL_OPEN_CLASSES : !isCollapsed ? 'rounded-b-lg' : '',
	);

	const showQuestDot = section.name === 'Quests' && hasClaimableQuests;
	const showReferralDot = section.name === 'Referral' && hasReferralToClaim;
	const capySrc = showCapyAboveMenu && capyImage ? capyImage : null;

	return (
		<motion.li className="relative" variants={ITEM_VARIANTS}>
			{capySrc ? <img src={capySrc} alt="Capy mascot" className="pointer-events-none absolute w-11 -translate-y-full right-0 select-none" /> : null}
			{showQuestDot && (
				<div className="absolute right-1 top-1 z-10">
					<div className="h-2.5 w-2.5 rounded-full bg-destructive" />
				</div>
			)}
			{showReferralDot && (
				<div className="absolute right-1 top-1 z-10">
					<div className="h-2.5 w-2.5 rounded-full bg-destructive" />
				</div>
			)}
			{isCollapsed ? (
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							onClick={onToggle}
							type="button"
							size="icon"
							variant="ghost"
							aria-label={section.name}
							aria-expanded={isExpanded}
							className={collapsedTriggerClasses}
						>
							<MenuIcon iconType={section.iconType} className="h-5 w-5" />
						</Button>
					</TooltipTrigger>
					<TooltipContent side="right">
						<p>{section.name}</p>
					</TooltipContent>
				</Tooltip>
			) : isGamesCategory ? (
				<div
					className={cn(triggerClasses, 'cursor-pointer')}
					role="group"
					aria-expanded={isExpanded}
					onClick={(event) => {
						if (event.defaultPrevented) return;
						const target = event.target as HTMLElement | null;
						if (target?.closest('a')) return;
						onToggle();
					}}
				>
					<Link
						to="/games"
						className="flex flex-1 cursor-pointer items-center text-inherit transition-colors"
						data-drawer-target="default-sidebar"
						data-drawer-hide="default-sidebar"
						aria-controls="default-sidebar"
						onClick={(event) => {
							event.stopPropagation();
							onOpen();
						}}
					>
						<MenuIcon className="inline-block h-5 w-5 text-inherit" iconType={section.iconType} />
						<span className="ms-2 text-sm">Games</span>
					</Link>
					<button
						type="button"
						onClick={(event) => {
							event.stopPropagation();
							onToggle();
						}}
						className={cn(
							'ml-2 flex size-5 items-center justify-center rounded-xl border border-border/40 bg-muted/40 text-muted-foreground/90 shadow-xs transition-colors',
							'hover:bg-muted/60 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar',
						)}
						aria-label={isExpanded ? 'Collapse games menu' : 'Expand games menu'}
						aria-expanded={isExpanded}
					>
						<motion.div className="cursor-pointer" animate={{ rotate: isExpanded ? 90 : 0 }} transition={{ duration: 0.2 }}>
							<ChevronRight className="h-4 w-4" />
						</motion.div>
					</button>
				</div>
			) : (
				<Button onClick={onToggle} type="button" variant="ghost" className={triggerClasses} aria-expanded={isExpanded}>
					<div className="flex items-center">
						<MenuIcon iconType={section.iconType} />
						<span className="ms-2 text-sm font-semibold">{section.name}</span>
					</div>
					<motion.div animate={{ rotate: isExpanded ? 90 : 0 }} transition={{ duration: 0.2 }}>
						<ChevronRight className="h-4 w-4" />
					</motion.div>
				</Button>
			)}
			<motion.div
				initial={false}
				animate={{
					height: isExpanded ? 'auto' : 0,
					opacity: isExpanded ? 1 : 0,
				}}
				transition={{ duration: 0.3, ease: easeInOut }}
				className={wrapperClasses}
				onAnimationComplete={onAnimationComplete}
			>
				<motion.div variants={LIST_VARIANTS} initial="hidden" animate="visible" key={`${section.name}-${isCollapsed}`} className={panelClasses}>
					{section.items?.map((item) => {
						const isActive = locationPath === item.to || locationPath.startsWith(`${item.to}/`);
						const isHot = item.name === hotGameName;
						const trailingBadges: TrailingBadgeDescriptor[] = [];
						if (isHot) {
							trailingBadges.push({
								key: 'hot',
								label: 'Hot',
								className: 'rotate-2 text-[10px] font-bold uppercase py-0 px-1',
							});
						}
						if (maxXpMultiplier > 1) {
							trailingBadges.push({
								key: 'xp',
								label: <>XP ×{maxXpMultiplier.toFixed(2)}</>,
								className: 'rotate-2 text-[10px] font-bold py-0 px-1',
							});
						}
						if (item.isNew) {
							trailingBadges.push({
								key: 'menu-new',
								label: 'New!',
								variant: 'accent',
								className: 'rotate-1 text-[10px] font-bold uppercase tracking-wide py-0 px-1 shadow-sm',
							});
						}

						return (
							<GameMenuItem
								key={item.name}
								item={item}
								isCollapsed={isCollapsed}
								isCompactHeight={isCompactHeight}
								isActive={isActive}
								isHot={isHot}
								menuClasses={menuClasses}
								trailingBadges={trailingBadges}
								hoveredGame={hoveredGame}
								setHoveredGame={setHoveredGame}
								closeSidebarForMobile={closeSidebarForMobile}
							/>
						);
					})}
				</motion.div>
			</motion.div>
		</motion.li>
	);
}

type MenuLinkItemProps = {
	section: MenuLinkSection;
	isCollapsed: boolean;
	isCompactHeight: boolean;
	locationPath: string;
	closeSidebarForMobile: () => void;
	menuClasses: MenuUiClasses;
	vipProfileLevel: number;
	weeklyTimeLeftShort: string;
	hasClaimableQuests: boolean;
	hasReferralToClaim: boolean;
	nftBadgeConfig?: {
		show: boolean;
		dismiss: () => void;
	};
};

function MenuLinkItem({
	section,
	isCollapsed,
	isCompactHeight,
	locationPath,
	closeSidebarForMobile,
	menuClasses,
	vipProfileLevel,
	weeklyTimeLeftShort,
	hasClaimableQuests,
	hasReferralToClaim,
	nftBadgeConfig,
}: MenuLinkItemProps) {
	const { nav, collapsed } = menuClasses;
	const isDisabled = Boolean(section.disabled);
	const comingSoonLabel = isDisabled ? (section.comingSoonLabel ?? 'Soon') : null;
	const isActive = !isDisabled && (locationPath === section.to || locationPath.startsWith(`${section.to}/`));
	const showVipBadge = section.name === 'VIP' && vipProfileLevel > 0;
	const showWeeklyBadge = !isDisabled && section.name === 'Weekly Race' && !!weeklyTimeLeftShort;
	const showQuestsDot = !isDisabled && section.name === 'Quests' && hasClaimableQuests;
	const showReferralDot = !isDisabled && section.name === 'Referral' && hasReferralToClaim;
	const showNftBadge = !isDisabled && section.name === 'NFT' && nftBadgeConfig?.show;
	const showComingSoonBadge = Boolean(comingSoonLabel);
	const showTrailingIndicators = showVipBadge || showWeeklyBadge || showQuestsDot || showReferralDot || showComingSoonBadge || showNftBadge;

	const handleNavigate = () => {
		closeSidebarForMobile();
		if (!isDisabled && section.name === 'NFT') {
			nftBadgeConfig?.dismiss();
		}
	};

	const trailingIndicators = showTrailingIndicators ? (
		<div className="ml-auto flex items-center gap-1">
			{showComingSoonBadge ? (
				<Badge variant="secondary" className="px-1.5 py-0 text-[10px] font-semibold uppercase tracking-wide">
					{comingSoonLabel}
				</Badge>
			) : null}
			{showVipBadge && (
				<AttentionBadge variant="secondary" className="rotate-2 text-[10px] font-bold py-0 px-1" delay={0.55}>
					LVL {vipProfileLevel}
				</AttentionBadge>
			)}
			{showWeeklyBadge && (
				<AttentionBadge
					variant="secondary"
					className="rotate-2 text-[10px] font-bold py-0 px-1"
					delay={showVipBadge ? 0.75 : 0.55}
					staggerIndex={showVipBadge ? 1 : 0}
				>
					{weeklyTimeLeftShort}
				</AttentionBadge>
			)}
			{showQuestsDot && <div className="h-2.5 w-2.5 rounded-full bg-destructive" />}
			{showReferralDot && <div className="h-2.5 w-2.5 rounded-full bg-destructive" />}
			{showNftBadge && (
				<Badge variant="destructive" className="px-1.5 py-0 text-[10px] font-black uppercase leading-none">
					!
				</Badge>
			)}
		</div>
	) : null;

	const collapsedNftBadge = showNftBadge ? (
		<span className="pointer-events-none absolute -right-0.5 -top-0.5 flex h-4 w-4 items-center justify-center rounded-full bg-destructive text-[10px] font-black text-white shadow-sm">
			!
		</span>
	) : null;

	const content = (
		<>
			<MenuIcon iconType={section.iconType} className={cn('inline-block size-5', isActive ? 'text-primary-foreground' : 'text-muted-foreground')} />
			<span className="text-sm">{section.name}</span>
			{trailingIndicators}
		</>
	);

	if (isCollapsed) {
		return (
			<motion.li className="relative" variants={ITEM_VARIANTS}>
				{collapsedNftBadge}
				<Tooltip>
					<TooltipTrigger asChild>
						{isDisabled ? (
							<Button
								type="button"
								variant="ghost"
								size="icon"
								className={cn(collapsed.base, 'cursor-not-allowed opacity-60')}
								disabled
								aria-disabled
							>
								<MenuIcon iconType={section.iconType} className="inline-block size-5 text-muted-foreground" />
							</Button>
						) : (
							<Button asChild variant="ghost" size="icon" className={cn(collapsed.base, isActive ? collapsed.active : collapsed.inactive)}>
								<Link
									to={section.to}
									className="flex h-full w-full items-center justify-center"
									aria-controls="default-sidebar"
									aria-label={section.name}
									onClick={handleNavigate}
								>
									<MenuIcon
										iconType={section.iconType}
										className={cn('inline-block size-5', isActive ? 'text-primary-foreground' : 'text-muted-foreground')}
									/>
								</Link>
							</Button>
						)}
					</TooltipTrigger>
					<TooltipContent side="right">
						<p>{section.name}</p>
						{comingSoonLabel ? <p className="text-xs text-muted-foreground/80">{comingSoonLabel}</p> : null}
					</TooltipContent>
				</Tooltip>
			</motion.li>
		);
	}

	if (isDisabled) {
		return (
			<motion.li className="relative" variants={ITEM_VARIANTS}>
				<Button
					type="button"
					variant="ghost"
					size="sm"
					className={cn(nav.base, nav.inactive, 'cursor-not-allowed opacity-70', isCompactHeight ? 'px-2 py-1.5' : '')}
					disabled
					aria-disabled
				>
					{content}
				</Button>
			</motion.li>
		);
	}

	return (
		<motion.li className="relative" variants={ITEM_VARIANTS}>
			<Button asChild variant="ghost" size="sm" className={cn(nav.base, isActive ? nav.active : nav.inactive, isCompactHeight ? 'px-2 py-1.5' : '')}>
				<Link to={section.to} className="flex w-full items-center justify-start gap-2" aria-controls="default-sidebar" onClick={handleNavigate}>
					{content}
				</Link>
			</Button>
		</motion.li>
	);
}

function PromoMenuButton({
	isCollapsed,
	menuClasses,
	label,
	onClick,
	showPulse,
	imageSrc = slushCoin,
	imageAlt = label,
	iconSrc,
	imageClassName,
	iconClassName,
	trailingBadge,
	useImageIcon = false,
}: {
	isCollapsed: boolean;
	menuClasses: MenuUiClasses;
	label: string;
	onClick: () => void;
	showPulse: boolean;
	imageSrc?: string;
	imageAlt?: string;
	iconSrc?: string;
	imageClassName?: string;
	iconClassName?: string;
	trailingBadge?: ReactNode;
	useImageIcon?: boolean;
}) {
	const { nav, collapsed } = menuClasses;
	const resolvedIconSrc = iconSrc ?? imageSrc;
	const icon = useImageIcon ? <img src={resolvedIconSrc} alt="" className={cn('size-5 shrink-0 object-contain', iconClassName)} /> : <GiftIcon className="size-5" />;

	if (isCollapsed) {
		return (
			<motion.li className="relative" variants={ITEM_VARIANTS}>
				<Tooltip>
					<TooltipTrigger asChild>
						<Button
							type="button"
							variant="ghost"
							size="icon"
							className={cn(collapsed.base, collapsed.active, 'relative overflow-visible')}
							onClick={onClick}
							aria-label={label}
						>
							{showPulse ? <PromoPulseHalo variant="collapsed" /> : null}
							{icon}
							{trailingBadge ? <span className="pointer-events-none absolute -right-2 -top-2 z-10">{trailingBadge}</span> : null}
						</Button>
					</TooltipTrigger>
					<TooltipContent side="right">
						<p>{label}</p>
					</TooltipContent>
				</Tooltip>
			</motion.li>
		);
	}

		return (
			<motion.li className="group/promo relative" variants={ITEM_VARIANTS}>
				<img
					src={imageSrc}
					alt={imageAlt}
					className={cn('pointer-events-none absolute right-4 w-36 -translate-y-full select-none', imageClassName)}
				/>

			<Button
				type="button"
				variant="ghost"
				size="sm"
				className={cn(nav.base, nav.active, 'relative overflow-visible font-semibold bg-primary')}
				onClick={onClick}
			>
				{showPulse ? <PromoPulseHalo variant="expanded" /> : null}
				{useImageIcon ? (
					<img src={resolvedIconSrc} alt="" className={cn('size-5 shrink-0 object-contain', iconClassName)} />
				) : (
					<GiftIcon className="size-5 text-primary-foreground" />
				)}
				<span className="text-base font-semibold leading-none tracking-tight text-primary-foreground">{label}</span>
				{trailingBadge ? <span className="ml-auto shrink-0">{trailingBadge}</span> : null}
			</Button>
		</motion.li>
	);
}

function PromoTicketBadge({ countLabel }: { countLabel: string }) {
	return (
		<span className="inline-flex items-center gap-1 rounded-full border border-border/50 bg-background/90 px-2 py-1 text-[11px] font-semibold leading-none text-foreground shadow-sm">
			<img src={ticketIconUrl} alt="" className="size-3.5 shrink-0 object-contain" />
			<span>{countLabel}</span>
		</span>
	);
}

function PromoPulseHalo({ variant }: { variant: 'collapsed' | 'expanded' }) {
	const baseInset = variant === 'collapsed' ? 'inset-[-8px]' : 'inset-[-10px]';
	const radius = variant === 'collapsed' ? 'rounded-full' : 'rounded-2xl';
	return (
		<>
			{[0, 0.5].map((delay) => (
				<motion.span
					aria-hidden
					key={delay}
					className={cn(
						'pointer-events-none absolute border border-primary/60 shadow-[0_0_30px_rgba(107,83,255,0.45)] bg-primary/5',
						baseInset,
						radius,
					)}
					initial={{ opacity: 0.35, scale: 0.85 }}
					animate={{ opacity: [0.55, 0], scale: [1, 1.25] }}
					transition={{ duration: 1.8, repeat: Infinity, repeatDelay: 0.2, ease: easeInOut, delay }}
				/>
			))}
		</>
	);
}
